import { Component, Input, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import app from 'firebase/compat/app';

import {
    RoomAuditLogEntry, RoomAuditLogEntryType, TerpecaCategory, TerpecaRoom, getRoomAuditLogString, splitClosedReason
} from 'src/app/models/room.model';
import { ApplicationStatus } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { RoomRestrictedService } from 'src/app/services/room-restricted.service';
import { SettingsService } from 'src/app/services/settings.service';
import * as utils from 'src/app/utils/misc.utils';
import { RankComponent } from 'src/app/views/rank/rank.component';
import { ReviewRoomsComponent } from 'src/app/views/reviewrooms/reviewrooms.component';
import { RoomEditorComponent } from 'src/app/views/roomeditor/roomeditor.component';
import { environment } from 'src/environments/environment';

import { NominateComponent } from '../nominate/nominate.component';

@Component({
  selector: 'app-room',
  standalone: false,
  templateUrl: './room.component.html',
  styleUrl: './room.component.css'
})
export class RoomComponent {
  private auth = inject(AuthService);
  private rooms = inject(RoomRestrictedService);
  private settings = inject(SettingsService);
  private dialog = inject(MatDialog);

  @Input() room: TerpecaRoom;
  @Input() matcher: ReviewRoomsComponent;
  @Input() ranker: RankComponent;
  @Input() nominate: NominateComponent;
  @Input() forDisclosure: boolean = false;
  @Input() allowSelect: boolean = false;
  @Input() disableSelect: boolean = false;
  @Input() allowEdit: boolean = false;
  @Input() allowProcess: boolean = false;
  @Input() selected: boolean = false;
  @Input() showNotes: boolean = false;
  @Input() selectLabel: string = 'Select';
  @Input() unselectLabel: string = 'Unselect';

  getRoomAuditLogString = getRoomAuditLogString;
  year = environment.currentAwardYear;
  editable = false;

  backgroundColor() {
    if (this.selected) {
      return 'lightgrey';
    }
    if (this.matcher && this.isFinalized()) {
      return 'whitesmoke';
    }
    return 'inherit';
  }

  location() {
    return utils.getLocationString(this.room);
  }

  languages() {
    return utils.getLanguages(this.room);
  }

  previousYears() {
    const years: number[] = [];
    for (let y = 1998; y < this.year; ++y) {
      if (this.room.isApproved?.includes(y) || this.room.isIneligible?.includes(y) ||
          this.room.isPermanentlyIneligible?.[0] === y) {
        years.push(y);
      }
    }
    return years;
  }

  // Newest first: audit log entries from the calendar year of the latest entry (max 5).
  recentAuditLogEntries(): RoomAuditLogEntry[] {
    const latest = this.room.auditLogEntry;
    if (!latest) {
      return [];
    }
    // A just-saved entry has a pending server timestamp, so treat it as this year.
    const latestYear = latest.timestamp ? latest.timestamp.toDate().getFullYear() : new Date().getFullYear();
    // The latest entry is copied into auditLogHistory by onRoomUpdated, so skip it there.
    const isLatest = (entry: RoomAuditLogEntry) =>
      entry.entryType === latest.entryType && entry.uid === latest.uid && entry.note === latest.note &&
      (entry.timestamp && latest.timestamp ? entry.timestamp.isEqual(latest.timestamp) : !entry.timestamp && !latest.timestamp);
    const older = (this.room.auditLogHistory || [])
      .filter(entry => !isLatest(entry) && entry.timestamp && entry.timestamp.toDate().getFullYear() === latestYear)
      .sort((a, b) => b.timestamp.toMillis() - a.timestamp.toMillis());
    return [latest, ...older].slice(0, 5);
  }

  async save(formValue: any, entryType: RoomAuditLogEntryType) {
    const room = formValue || { };
    if (formValue?.name) {
      room.isConfirmedOpen = formValue.confirmedOpen ? <number><unknown>app.firestore.FieldValue.arrayUnion(this.year) :
            <number><unknown>app.firestore.FieldValue.arrayRemove(this.year);
      delete room.confirmedOpen;
      room.isConfirmedEnglish = formValue.confirmedEnglish ? <number><unknown>app.firestore.FieldValue.arrayUnion(this.year) :
            <number><unknown>app.firestore.FieldValue.arrayRemove(this.year);
      delete room.confirmedEnglish;
    }
    const reviewerNotes = formValue?.notes || null;
    const previousReason = this.room.ineligibilityReason || '';
    let nextReason = typeof formValue?.ineligibilityReason === 'string' ? (formValue.ineligibilityReason || '') : null;
    if (entryType === RoomAuditLogEntryType.MARKED_REOPENED) {
      const { other } = splitClosedReason(previousReason);
      const stillDq = Boolean(this.room.isIneligible?.includes(this.year));
      if (other && stillDq) {
        nextReason = other;
      } else if (!stillDq) {
        nextReason = '';
      } else {
        nextReason = null;
      }
    } else if (entryType === RoomAuditLogEntryType.UNDISQUALIFIED && !this.isPermanentlyClosed()) {
      nextReason = '';
    }
    const reasonChanged = nextReason !== null && nextReason !== previousReason;
    const includeReason = reasonChanged ||
      entryType === RoomAuditLogEntryType.DISQUALIFIED ||
      entryType === RoomAuditLogEntryType.MARKED_CLOSED;
    if (entryType !== RoomAuditLogEntryType.UPDATED || reviewerNotes || reasonChanged) {
      const reasonNote = includeReason ? (nextReason || '(cleared)') : null;
      const note = [reasonNote, reviewerNotes].filter(Boolean).join(' - ') || null;
      room.auditLogEntry = this.newRoomAuditLogEntry(entryType, note);
      delete room.notes;
    }
    if (nextReason === '') {
      room.ineligibilityReason = <string><unknown>app.firestore.FieldValue.delete();
    } else if (nextReason !== null) {
      room.ineligibilityReason = nextReason;
    }
    switch (entryType) {
      case RoomAuditLogEntryType.APPROVED:
        room.isApproved = <number><unknown>app.firestore.FieldValue.arrayUnion(this.year);
        break;
      case RoomAuditLogEntryType.UNAPPROVED:
        if (this.isFinalized()) {
          console.error(`can't unapprove a room that is already finalized!`);
          return;
        }
        room.isApproved = <number><unknown>app.firestore.FieldValue.arrayRemove(this.year);
        break;
      case RoomAuditLogEntryType.DISQUALIFIED:
        room.isApproved = <number><unknown>app.firestore.FieldValue.arrayRemove(this.year);
        room.isNominee = <number><unknown>app.firestore.FieldValue.arrayRemove(this.year);
        room.isFinalist = <number><unknown>app.firestore.FieldValue.arrayRemove(this.year);
        room.isIneligible = <number><unknown>app.firestore.FieldValue.arrayUnion(this.year);
        break;
      case RoomAuditLogEntryType.MARKED_CLOSED:
        room.isPermanentlyIneligible = [this.settings.isPastVotingDeadline() ? this.year + 1 : this.year];
        break;
      case RoomAuditLogEntryType.MARKED_REOPENED:
        room.isPermanentlyIneligible = [];
        break;
      case RoomAuditLogEntryType.FINALIZED:
        if (!this.isApproved()) {
          console.error(`can't finalize a room that isn't yet approved!`);
          return;
        }
        if (this.isIneligible()) {
          console.error(`can't finalize a room that has been disqualified!`);
          return;
        }
        if (!this.isAutomaticFinalist() && this.numCurrentNominations() === 0) {
          console.error(`can't finalize a room with no nominations!`);
          return;
        }
        room.isNominee = <number><unknown>app.firestore.FieldValue.arrayUnion(this.year);
        room.isFinalist = (!this.isAutomaticFinalist() && this.numCurrentNominations() < this.settings.finalistThreshold()) ?
          <number><unknown>app.firestore.FieldValue.arrayRemove(this.year) :
          <number><unknown>app.firestore.FieldValue.arrayUnion(this.year);
        break;
      case RoomAuditLogEntryType.UNDISQUALIFIED:
      case RoomAuditLogEntryType.UNFINALIZED:
        room.isNominee = <number><unknown>app.firestore.FieldValue.arrayRemove(this.year);
        room.isFinalist = <number><unknown>app.firestore.FieldValue.arrayRemove(this.year);
        room.isIneligible = <number><unknown>app.firestore.FieldValue.arrayRemove(this.year);
        break;
    }
    await this.rooms.updateRoom(this.room.docId, <Partial<TerpecaRoom>>room);
  }

  newRoomAuditLogEntry(type: RoomAuditLogEntryType, note?: string): RoomAuditLogEntry {
    return {
      uid: this.auth.currentUser.uid,
      name: this.auth.currentUser.realName || this.auth.currentUser.displayName,
      entryType: type,
      timestamp: <app.firestore.Timestamp>app.firestore.FieldValue.serverTimestamp(),
      year: this.year,
      note,
    };
  }

  canApprove() {
    return this.auth.currentUser && this.auth.currentUser.status >= ApplicationStatus.REVIEWER && this.matcher;
  }

  canDisqualify() {
    return this.auth.currentUser && this.auth.currentUser.status >= ApplicationStatus.REVIEWER && this.matcher;
  }

  canEditCompanyNames() {
    return this.auth.currentUser && this.auth.currentUser.status >= ApplicationStatus.APPROVER &&
      this.matcher && !this.matcher.isViewingAsReviewer();
  }

  canSeeNominationCounts() {
    return this.auth.currentUser && this.matcher && !this.matcher.isViewingAsReviewer() &&
      (this.auth.currentUser.isOwner || this.auth.currentUser.status >= ApplicationStatus.APPROVER);
  }

  canFinalize() {
    return this.settings.isPastNominationDeadline() && this.auth.currentUser &&
           this.auth.currentUser.status >= ApplicationStatus.APPROVER && this.matcher &&
           !this.matcher.isViewingAsReviewer();
  }

  canUnfinalize() {
    return !this.settings.isVotingOpen(this.auth.currentUser) &&
      (this.isNominee() || this.isFinalist() || this.isIneligible()) &&
      this.auth.currentUser && this.auth.currentUser.status >= ApplicationStatus.REVIEWER && this.matcher;
  }

  isOnlineRoom() {
    return this.room.category === TerpecaCategory.TOP_ONLINE_ROOM;
  }

  isInPersonRoom() {
    return this.room.category === TerpecaCategory.TOP_ROOM;
  }

  isCompany() {
    return this.room.category === TerpecaCategory.TOP_COMPANY;
  }

  isApproved(year?: number) {
    return utils.isApproved(this.room, year);
  }

  isIneligible(year?: number) {
    return utils.isIneligible(this.room, year);
  }

  isPermanentlyClosed(year?: number) {
    return utils.isPermanentlyClosed(this.room, year);
  }

  isAutomaticFinalist(year?: number) {
    return utils.isAutomaticFinalist(this.room, year);
  }

  isNominee(year?: number) {
    return utils.isNominee(this.room, year);
  }

  isFinalist(year?: number) {
    return utils.isFinalist(this.room, year);
  }

  isWinner(year?: number) {
    return utils.isWinner(this.room, year);
  }

  isFinalized() {
    if (this.isIneligible() || this.isNominee() || this.isFinalist()) {
      return true;
    }
    if (!this.isApproved() || !this.settings.isPastNominationDeadline()) {
      return false;
    }
    // Reviewers can't see current-year nominations or nominee/finalist flags (#297), so any
    // approved room past the deadline may already be finalized (including automatic finalists).
    if (this.matcher && !this.matcher.canSeeRestrictedRoomData()) {
      return true;
    }
    return !this.isAutomaticFinalist() && this.numCurrentNominations() === 0;
  }

  private async saveWithScrollPreservation(event: Event | undefined, entryType: RoomAuditLogEntryType) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Save scroll position
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    await this.save(null, entryType);
    // Restore scroll position after Angular finishes re-rendering
    // Use double requestAnimationFrame to ensure DOM updates are complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    });
  }

  async finalize(event?: Event) {
    await this.saveWithScrollPreservation(event, RoomAuditLogEntryType.FINALIZED);
  }

  async unfinalize(event?: Event) {
    await this.saveWithScrollPreservation(event, RoomAuditLogEntryType.UNFINALIZED);
  }

  edit() {
    this.dialog.open(RoomEditorComponent, { data: { parent: this } });
  }

  toggleSelected() {
    if (this.matcher) {
      this.matcher.toggleSelectedRoom(this.room.docId);
    }
    if (this.ranker) {
      if (this.forDisclosure) {
        this.ranker.toggleAffiliatedRoom(this.room.docId);
      } else {
        this.ranker.togglePlayedRoom(this.room.category, this.room.docId);
      }
    }
    if (this.nominate) {
      this.nominate.nominateExistingRoom(this.room);
    }
  }

  numTotalNominations() {
    return utils.getNominationCount(this.room);
  }

  numCurrentNominations() {
    return utils.getNominationCount(this.room, this.year);
  }
}
